import { useState, useEffect, useRef } from "react";
import { Box, Typography, CircularProgress, Chip } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import MDButton from "../../components/MDButton";
import apiService from "../../services/api-service";

const MARKER_COLORS = {
	APERTA: "#ff9800",
	RISPOSTA: "#2196f3",
	APPROVATA: "#4caf50",
};

const FloorPlanView = () => {
	const { areaId, floorPlanId } = useParams();
	const navigate = useNavigate();
	const imageRef = useRef(null);
	const [floorPlan, setFloorPlan] = useState(null);
	const [snagItems, setSnagItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [hoveredSnag, setHoveredSnag] = useState(null);

	useEffect(() => {
		fetchData();
	}, [floorPlanId]);

	const fetchData = async () => {
		setLoading(true);
		try {
			const [fpResponse, snagResponse] = await Promise.all([
				apiService.get(`/snaglist/floor-plans/${floorPlanId}/`, {}, {}),
				apiService.get(`/snaglist/floor-plans/${floorPlanId}/snag-items/`, {}, {}),
			]);
			setFloorPlan(fpResponse);
			setSnagItems(snagResponse || []);
		} catch (error) {
			toast.error("Errore caricamento planimetria");
		} finally {
			setLoading(false);
		}
	};

	const handleImageClick = (e) => {
		if (!imageRef.current) return;
		const rect = imageRef.current.getBoundingClientRect();
		if (rect.width === 0 || rect.height === 0) return;
		const x = Math.round(((e.clientX - rect.left) / rect.width) * 10000) / 100;
		const y = Math.round(((e.clientY - rect.top) / rect.height) * 10000) / 100;
		console.log("Snag click coords:", { x, y, rectW: rect.width, rectH: rect.height });
		navigate(`/snag-list/add/${floorPlanId}`, {
			state: { x, y },
		});
	};

	const handleMarkerClick = (e, snag) => {
		e.stopPropagation();
		setHoveredSnag(hoveredSnag?.id === snag.id ? null : snag);
	};

	if (loading) {
		return (
			<DashboardLayout>
				<DashboardNavbar />
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
					<CircularProgress />
				</Box>
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<Box p={3}>
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
					<Box display="flex" alignItems="center" gap={2}>
						<MDButton
							variant="outlined"
							color="info"
							startIcon={<ArrowBack />}
							onClick={() => navigate(`/snag-list`)}
						>
							Torna alla lista
						</MDButton>
						<Typography variant="h4" color="primary">
							{floorPlan?.name || "Planimetria"}
						</Typography>
					</Box>
					<Box display="flex" gap={1}>
						<Chip
							size="small"
							sx={{ bgcolor: MARKER_COLORS.APERTA, color: "white" }}
							label="Aperta"
						/>
						<Chip
							size="small"
							sx={{ bgcolor: MARKER_COLORS.RISPOSTA, color: "white" }}
							label="Risposta"
						/>
						<Chip
							size="small"
							sx={{ bgcolor: MARKER_COLORS.APPROVATA, color: "white" }}
							label="Approvata"
						/>
					</Box>
				</Box>

				<Typography variant="body2" color="textSecondary" mb={2}>
					Clicca sulla planimetria per aggiungere un nuovo snag
				</Typography>

				{/* Floor plan with markers */}
				<Box
					sx={{
						position: "relative",
						width: "100%",
						cursor: "crosshair",
						borderRadius: 2,
						overflow: "hidden",
						boxShadow: 3,
					}}
					onClick={handleImageClick}
				>
					{floorPlan?.image_url && (
						<img
							ref={imageRef}
							src={floorPlan.image_url}
							alt={floorPlan.name}
							style={{
								width: "100%",
								display: "block",
								userSelect: "none",
								pointerEvents: "none",
							}}
							draggable={false}
						/>
					)}

					{/* Snag markers */}
					{snagItems.map((snag) => (
						<Box
							key={snag.id}
							onClick={(e) => handleMarkerClick(e, snag)}
							sx={{
								position: "absolute",
								left: `${snag.x_percent}%`,
								top: `${snag.y_percent}%`,
								transform: "translate(-50%, -50%)",
								bgcolor: MARKER_COLORS[snag.status] || MARKER_COLORS.APERTA,
								color: "white",
								borderRadius: "12px",
								px: 1,
								py: 0.25,
								fontSize: "0.7rem",
								fontWeight: "bold",
								cursor: "pointer",
								zIndex: 10,
								minWidth: "36px",
								textAlign: "center",
								boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
								border: "2px solid white",
								"&:hover": {
									transform: "translate(-50%, -50%) scale(1.2)",
									zIndex: 20,
								},
								transition: "transform 0.15s ease",
							}}
						>
							{snag.code}
						</Box>
					))}

					{/* Popover for hovered snag */}
					{hoveredSnag && (
						<Box
							onClick={(e) => e.stopPropagation()}
							sx={{
								position: "absolute",
								left: `${hoveredSnag.x_percent}%`,
								top: `${hoveredSnag.y_percent}%`,
								transform: "translate(-50%, calc(-100% - 20px))",
								bgcolor: "white",
								borderRadius: 1,
								p: 1.5,
								boxShadow: 4,
								zIndex: 30,
								minWidth: 180,
								maxWidth: 250,
							}}
						>
							<Typography variant="subtitle2" fontWeight="bold">
								{hoveredSnag.code}
							</Typography>
							<Chip
								size="small"
								label={hoveredSnag.status}
								sx={{
									bgcolor: MARKER_COLORS[hoveredSnag.status],
									color: "white",
									mb: 0.5,
								}}
							/>
						</Box>
					)}
				</Box>
			</Box>
		</DashboardLayout>
	);
};

export default FloorPlanView;
